import {
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  Box,
  Chip,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { InfoCard } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';

const useStyles = makeStyles(theme => ({
  deploymentsCard: {
    height: '100%',
  },
  splitContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    height: '100%',
  },
  leftSection: {
    flex: 1,
    minWidth: 0,
  },
  rightSection: {
    flex: 1,
    minWidth: 0,
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  scrollableTableContainer: {
    overflowX: 'auto',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.background.default,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  statusChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  statusDeployed: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  statusUpdating: {
    backgroundColor: '#ff9800',
    color: '#fff',
  },
  statusDeploying: {
    backgroundColor: '#2196f3',
    color: '#fff',
  },
  statusFailed: {
    backgroundColor: '#f44336',
    color: '#fff',
  },
  statusNew: {
    backgroundColor: '#9e9e9e',
    color: '#fff',
  },
  statusInitializing: {
    backgroundColor: '#607d8b',
    color: '#fff',
  },
  timestampCell: {
    whiteSpace: 'nowrap',
    cursor: 'help',
  },
}));

type DeploymentInfo = {
  service: string;
  version: string;
  environment: string;
  cluster: string;
  status: string;
  timestamp: string;
};

export const DeploymentsCard = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  // Fetch DeploymentManifest entities
  const { value: deploymentsData, loading, error } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: {
        kind: 'DeploymentManifest',
      },
    });

    // Transform deployments
    const deploymentList: DeploymentInfo[] = response.items
      .map((entity: Entity) => {
        const serviceName =
          (entity.spec as any)?.component ||
          entity.metadata.annotations?.['sitecore.com/service-name'] ||
          entity.metadata.name;
        
        // Get version from the correct annotation (populated from deployedVersion API field)
        const version =
          entity.metadata.annotations?.['sitecore.com/deployed-version'] ||
          entity.metadata.annotations?.['sitecore.com/deploying-version'] ||
          '—';
        
        const environment =
          (entity.spec as any)?.environment ||
          entity.metadata.annotations?.['sitecore.com/environment'] ||
          '—';

        const cluster =
          (entity.spec as any)?.cluster ||
          entity.metadata.labels?.['sitecore.com/cluster'] ||
          '—';

        const status =
          entity.metadata.annotations?.['sitecore.com/status'] ||
          entity.metadata.labels?.['sitecore.com/status'] ||
          '—';

        // Use the most recent timestamp available
        const timestamp = 
          entity.metadata.annotations?.['backstage.io/updated-at'] ||
          entity.metadata.annotations?.['backstage.io/created-at'] ||
          new Date().toISOString();

        return {
          service: serviceName,
          version,
          environment,
          cluster,
          status,
          timestamp,
        };
      });

    // Left side: Recent deployed deployments (only "Deployed" status)
    const recentDeployed = deploymentList
      .filter(d => d.status.toLowerCase() === 'deployed')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 10);

    // Right side: All deployments sorted alphabetically by service name
    const allDeployments = deploymentList
      .sort((a, b) => a.service.localeCompare(b.service));

    return { recentDeployed, allDeployments };
  });

  if (error) {
    return (
      <InfoCard title="Deployments" className={classes.deploymentsCard}>
        <CardContent>
          <Typography color="error">
            Failed to load deployments. Please try refreshing the page.
          </Typography>
        </CardContent>
      </InfoCard>
    );
  }

  if (loading) {
    return (
      <InfoCard title="Deployments" className={classes.deploymentsCard}>
        <CardContent>
          <Box className={classes.emptyState}>
            <Typography>Loading deployments...</Typography>
          </Box>
        </CardContent>
      </InfoCard>
    );
  }

  if (!deploymentsData || (deploymentsData.recentDeployed.length === 0 && deploymentsData.allDeployments.length === 0)) {
    return (
      <InfoCard title="Deployments" className={classes.deploymentsCard}>
        <CardContent>
          <Box className={classes.emptyState}>
            <Typography>No deployments found</Typography>
          </Box>
        </CardContent>
      </InfoCard>
    );
  }

  const getStatusChipClass = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    if (normalizedStatus === 'deployed') return classes.statusDeployed;
    if (normalizedStatus === 'updating' || normalizedStatus === 'triggered') return classes.statusUpdating;
    if (normalizedStatus === 'deploying') return classes.statusDeploying;
    if (normalizedStatus === 'failed') return classes.statusFailed;
    if (normalizedStatus === 'new') return classes.statusNew;
    if (normalizedStatus === 'initializing') return classes.statusInitializing;
    return classes.statusNew;
  };

  const { recentDeployed, allDeployments } = deploymentsData;

  return (
    <InfoCard title="Deployments" className={classes.deploymentsCard}>
      <CardContent>
        <Box className={classes.splitContainer}>
          {/* Left Section - Recent Deployed */}
          <Box className={classes.leftSection}>
            <Typography className={classes.sectionTitle}>
              Recent Deployed
            </Typography>
            <Box className={classes.tableContainer}>
              {recentDeployed.length === 0 ? (
                <Box className={classes.emptyState}>
                  <Typography variant="body2">No deployed services</Typography>
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headerCell}>SERVICE</TableCell>
                      <TableCell className={classes.headerCell}>VERSION</TableCell>
                      <TableCell className={classes.headerCell}>ENVIRONMENT</TableCell>
                      <TableCell className={classes.headerCell}>CLUSTER</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentDeployed.map((deployment, index) => (
                      <TableRow key={`recent-${deployment.service}-${deployment.environment}-${deployment.cluster}-${index}`}>
                        <TableCell>{deployment.service}</TableCell>
                        <TableCell>{deployment.version}</TableCell>
                        <TableCell>{deployment.environment}</TableCell>
                        <TableCell>{deployment.cluster}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Box>

          {/* Right Section - All Deployments (Scrollable) */}
          <Box className={classes.rightSection}>
            <Typography className={classes.sectionTitle}>
              All Deployments
            </Typography>
            <Box className={classes.scrollableTableContainer}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headerCell}>SERVICE</TableCell>
                    <TableCell className={classes.headerCell}>VERSION</TableCell>
                    <TableCell className={classes.headerCell}>ENVIRONMENT</TableCell>
                    <TableCell className={classes.headerCell}>CLUSTER</TableCell>
                    <TableCell className={classes.headerCell}>STATUS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allDeployments.map((deployment, index) => (
                    <TableRow key={`all-${deployment.service}-${deployment.environment}-${deployment.cluster}-${index}`}>
                      <TableCell>{deployment.service}</TableCell>
                      <TableCell>{deployment.version}</TableCell>
                      <TableCell>{deployment.environment}</TableCell>
                      <TableCell>{deployment.cluster}</TableCell>
                      <TableCell>
                        <Chip
                          label={deployment.status}
                          size="small"
                          className={`${classes.statusChip} ${getStatusChipClass(deployment.status)}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </InfoCard>
  );
};